import { useEffect, useRef, useState } from 'preact/hooks';

import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import { removalFromGarageRequested, sendVehicleDialogRequest } from 'Core/actions/fitmentSearch/index';
import { useDropdown } from 'Core/hooks/index';
import {
  fitmentSearchBaseFieldsSelector,
  fitmentSearchExtraFieldsSelector,
  garageEmptySelector,
  garageSizeSelector,
  isVehicleSelectedSelector,
  selectedVehicleSelector,
} from 'Core/selectors/fitmentSearch/index';
import { useDispatch, useSelector } from 'Core/store';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import { cloneSafe } from 'Utils/components';

import type { GarageVehicleTemplateParams } from '../types';
import type { BaseDropdownTemplateParams } from './dropdown/baseDropdown';
import Dropdown from './dropdown/index';

export const vehicleWidgetName = 'GarageVehicleWidget';
export const searchBoxWidgetName = 'GarageSearchBox';

const dropdownName = 'garageDropdown';

interface Props {
  template: TemplateFunction<GarageTemplateParams>;
  name: string;
  doNotRedirectOnVehicleSelect?: boolean;
  isAutoVehicleSelectionDisabled?: boolean;
  redirectUrl?: string;
  useNativeDropdown?: boolean;
  useVehicleWidget?: boolean;
}

export interface GarageTemplateParams extends GarageVehicleTemplateParams {
  name: string;
  size: number;
  garageDropdown: TemplateFunctionInvoker<BaseDropdownTemplateParams>;
  isDropdownVisible: boolean;
  isEmpty: boolean;
  isVehicleSelected: boolean;
  showVehicleDropdowns: () => void;
  hideVehicleDropdowns: () => void;
  isShowVehicleDropdowns: boolean;
  removeSelectedVehicle: () => void;
  openGarage: () => void;
  closeGarage: () => void;
}

export default function Garage({
  template,
  name,
  doNotRedirectOnVehicleSelect,
  isAutoVehicleSelectionDisabled = true,
  redirectUrl,
  useNativeDropdown,
  useVehicleWidget,
}: Props): TemplateResult {
  const rootRef = useRef<HTMLElement>(null);

  const dispatch = useDispatch();

  const [isShowVehicleDropdowns, setIsShowVehicleDropdowns] = useState(false);

  const {
    hideDropdown: closeGarage,
    showDropdown: openGarage,
    DO_NOT_USE_visible: isDropdownVisible,
  } = useDropdown(rootRef, '.cm_garage', {
    dropdownName,
    toggle: true,
  });

  const size = useSelector(garageSizeSelector);
  const isEmpty = useSelector(garageEmptySelector);

  const selectedVehicle = useSelector(selectedVehicleSelector);
  const isVehicleSelected = useSelector(isVehicleSelectedSelector);

  const baseFitmentFields = useSelector(fitmentSearchBaseFieldsSelector);
  const extraFitmentFields = useSelector(fitmentSearchExtraFieldsSelector);

  useEffect(() => {
    if (!isDropdownVisible) {
      setIsShowVehicleDropdowns(false);
    } else if (isDropdownVisible && (useVehicleWidget || isShowVehicleDropdowns)) {
      dispatch(sendVehicleDialogRequest());
    }
  }, [dispatch, isDropdownVisible, isShowVehicleDropdowns, useVehicleWidget]);

  const showVehicleDropdowns = () => setIsShowVehicleDropdowns(true);
  const hideVehicleDropdowns = () => setIsShowVehicleDropdowns(false);

  const dropdown = (templ: TemplateFunction<BaseDropdownTemplateParams>) => (
    <Dropdown
      closeGarage={closeGarage}
      doNotRedirectOnVehicleSelect={doNotRedirectOnVehicleSelect}
      isAutoVehicleSelectionDisabled={isAutoVehicleSelectionDisabled}
      isEmpty={isEmpty}
      isShowVehicleDropdowns={isShowVehicleDropdowns}
      isVehicleSelected={isVehicleSelected}
      redirectUrl={redirectUrl}
      showVehicleDropdowns={showVehicleDropdowns}
      useNativeDropdown={useNativeDropdown}
      useVehicleWidget={useVehicleWidget}
      vehicle={selectedVehicle.filteredFieldMap}
      template={templ}
      key="dropdown"
    />
  );

  const removeSelectedVehicle = () => {
    dispatch(removalFromGarageRequested(selectedVehicle, true));
    fitmentSearchConfig.onVehicleDiscarded();
  };

  const component = template.call({
    name,
    size,
    [dropdownName]: dropdown,
    isDropdownVisible,
    isEmpty,
    isSelected: isVehicleSelected,
    isVehicleSelected,
    vehicle: selectedVehicle.filteredFieldMap,
    vehicleString: selectedVehicle.toString(),
    vehicleBaseValues: selectedVehicle.getFilteredValues(baseFitmentFields),
    vehicleExtraValues: selectedVehicle.getFilteredValues(extraFitmentFields),
    vehicleLogo: selectedVehicle.logo,
    showVehicleDropdowns,
    hideVehicleDropdowns,
    isShowVehicleDropdowns,
    removeSelectedVehicle,
    openGarage,
    closeGarage,
  });

  return cloneSafe(component, rootRef);
}
